import { settingChangeSchema, settingViewSchema } from "@amtana/module";
import { z } from "zod";

import { oidcProviderSchema } from "./auth.schemas.ts";

// Google's and LinkedIn's OpenID Connect client, as staff see and change it in the back office
// (ADR-008): issuer, client id and client secret, each defaulting to its environment variable
// (GOOGLE_*, LINKEDIN_*). Auth owns the stored values and seals the secret at rest; the back
// office reaches them only through AuthApi. A secret is only ever replaced or cleared.

export const oidcProviderSettingsSchema = z.object({
  provider: oidcProviderSchema,
  /** Every value set, so a sign-in can start. */
  configured: z.boolean(),
  settings: z.object({
    issuer: settingViewSchema,
    clientId: settingViewSchema,
    clientSecret: settingViewSchema,
  }),
  /** Where the provider sends the browser back: register it with the provider's client. */
  callbackUrl: z.string(),
});
export type OidcProviderSettings = z.infer<typeof oidcProviderSettingsSchema>;

export const oidcSettingsSchema = z.object({ providers: z.array(oidcProviderSettingsSchema) });
export type OidcSettings = z.infer<typeof oidcSettingsSchema>;

/** Each field: a value to store, null to clear back to the environment, or left out. */
export const oidcSettingsChangesSchema = z.object({
  issuer: z.url("Enter the issuer's address, such as https://accounts.google.com.").nullable().optional(),
  clientId: settingChangeSchema.optional(),
  clientSecret: settingChangeSchema.optional(),
});
export type OidcSettingsChanges = z.infer<typeof oidcSettingsChangesSchema>;

export const updateOidcSettingsInputSchema = z.object({
  provider: oidcProviderSchema,
  changes: oidcSettingsChangesSchema,
});
export type UpdateOidcSettingsInput = z.infer<typeof updateOidcSettingsInputSchema>;

/** What asking the issuer for its discovery document found. */
export const oidcCheckSchema = z.object({
  provider: oidcProviderSchema,
  issuer: z.string(),
  ok: z.boolean(),
  /** Null when the document answered and names this issuer. */
  problem: z.enum(["unreachable", "refused", "not-oidc", "issuer-mismatch"]).nullable(),
});
export type OidcCheck = z.infer<typeof oidcCheckSchema>;
